import { useEffect, useRef, useState } from 'react';
import { ErrorScreen_DEPRECIATED } from '@/components/ErrorScreen';
import { LoadScreen } from '@/components/LoadScreen';
import { isNfcPermissionMissing } from '@/utils/nfcPermission';
import { postNfcPermissionNeeded } from '@/utils/paymentHandlerBridge';
import { AndroidScreen } from '../AndroidScreen';

type GateState = 'checking' | 'granted' | 'missing';

/**
 * Payment-Handler-window NFC gate for the legacy 3DS flow.
 *
 * A PH window (Chrome 149+) can USE an existing Web NFC grant but can never PROMPT for
 * one. The normal path (AndroidScreenWithNfcChecks) mounts the SDK withWebNfcChecks
 * HOC, whose on-mount NDEFReader.scan() probe dead-ends on an NFC error screen here.
 * So before mounting the tap, do a silent permissions.query() (mirrors the reference
 * provider kit's boot() check):
 *   - granted → render the tap flow directly, bypassing the HOC probe (the step that
 *     fails in a PH window, and unnecessary once the grant is confirmed);
 *   - missing → signal NFC_PERMISSION_NEEDED to the invoker via the SW bridge, which
 *     kicks out to the enable-NFC interstitial and later re-presents a fresh PH window.
 *
 * Only used inside the PH window (?c=1); the plain /3ds/ deep-link path stays on the
 * existing AndroidScreenWithNfcChecks flow. See docs/PAYMENT_HANDLER.md.
 */
export const PaymentHandlerNfcGate = ({ processId }: { processId: string }) => {
  const [state, setState] = useState<GateState>('checking');
  const signalledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    isNfcPermissionMissing().then((missing) => {
      if (!cancelled) setState(missing ? 'missing' : 'granted');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state === 'missing' && !signalledRef.current) {
      signalledRef.current = true;
      postNfcPermissionNeeded();
    }
  }, [state]);

  if (state === 'checking') return <LoadScreen />;
  if (state === 'missing') {
    // Chrome dismisses the PH window as soon as the SW resolves the response, so
    // this is only briefly visible; the invoker drives the enable-NFC flow next.
    return (
      <ErrorScreen_DEPRECIATED
        title="Finish in your browser"
        body="Allow NFC access in the window that opens, then return here to tap your card."
      />
    );
  }
  return <AndroidScreen processId={processId} />;
};
